/**
 * Resolve category names and display titles for cached insight items.
 */
import type { InsightItemShape } from '../components/insights/InsightCard';

const SPENDING_SPIKE_ID_RE = /^spending-spike-(.+)$/;
const SPENDING_SPIKE_TITLE_RE = /^(.+) Spending Spike$/;
const PLACEHOLDER_CATEGORY_NAMES = new Set(['uncategorised', 'uncategorized', 'unknown']);
const INSIGHT_DOLLAR_AMOUNT_RE =
  /([+\-]?)\$((?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?)/g;

function isPlaceholderCategoryName(name: string | undefined): boolean {
  if (!name) return true;
  return PLACEHOLDER_CATEGORY_NAMES.has(name.trim().toLowerCase());
}

function extractSpendingSpikeCategoryId(insightId: string | undefined): string | undefined {
  const categoryId = insightId?.match(SPENDING_SPIKE_ID_RE)?.[1];
  if (!categoryId || isPlaceholderCategoryName(categoryId)) return undefined;
  return categoryId;
}

export function buildCategoryNameMap(
  categories: Array<{ id: string; name: string }>
): Map<string, string> {
  return new Map(categories.map((c) => [c.id, c.name]));
}

/** Real spending category name for spike alerts (e.g. "Health Insurance"). */
export function resolveSpendingCategoryName(
  item: InsightItemShape,
  categoryMap?: Map<string, string>
): string | undefined {
  const categoryId = extractSpendingSpikeCategoryId(item.id);
  if (categoryId && categoryMap) {
    const resolved = categoryMap.get(categoryId);
    if (resolved && !isPlaceholderCategoryName(resolved)) return resolved;
  }

  if (item.drivers?.category && !isPlaceholderCategoryName(item.drivers.category)) {
    return item.drivers.category;
  }

  const fromCategories = item.categories?.[0];
  if (fromCategories && !isPlaceholderCategoryName(fromCategories)) {
    return fromCategories;
  }

  const titleMatch = item.title?.match(SPENDING_SPIKE_TITLE_RE);
  const fromTitle = titleMatch?.[1];
  if (fromTitle && !isPlaceholderCategoryName(fromTitle)) return fromTitle;

  return undefined;
}

/** Card title — uses resolved category for spending spike alerts. */
export function resolveInsightTitle(
  item: InsightItemShape,
  categoryMap?: Map<string, string>
): string {
  const fallback = item.title ?? item.goal_description ?? 'Insight';
  const categoryName = resolveSpendingCategoryName(item, categoryMap);

  if (categoryName && item.id?.startsWith('spending-spike-')) {
    return `${categoryName} Spending Spike`;
  }

  if (categoryName && fallback === 'Uncategorised Spending Spike') {
    return `${categoryName} Spending Spike`;
  }

  return fallback;
}

function applyResolvedCategoryToText(
  text: string | undefined,
  categoryName: string | undefined
): string | undefined {
  if (!text || !categoryName) return text;
  return text.replace(/\buncategorised\b/gi, categoryName.toLowerCase());
}

/** Format dollar amounts in insight copy, e.g. $62994.34 -> $62,994.34 */
export function formatInsightNumbers(text: string | undefined): string {
  if (!text) return '';

  return text.replace(
    INSIGHT_DOLLAR_AMOUNT_RE,
    (match, sign: string | undefined, amountPart: string) => {
      const numeric = Number(amountPart.replace(/,/g, ''));
      if (!Number.isFinite(numeric)) return match;

      const hasDecimals = amountPart.includes('.');
      const formatted = new Intl.NumberFormat('en-AU', {
        minimumFractionDigits: hasDecimals ? 2 : 0,
        maximumFractionDigits: hasDecimals ? 2 : 0,
      }).format(numeric);

      return `${sign ?? ''}$${formatted}`;
    }
  );
}

/** Card body — rewrites stale "uncategorised" copy when a real category is known. */
export function resolveInsightBody(
  item: InsightItemShape,
  categoryMap?: Map<string, string>
): string {
  const categoryName = resolveSpendingCategoryName(item, categoryMap);
  const raw =
    item.explanation ??
    item.summary ??
    item.rationale ??
    item.current_status ??
    item.goal_description ??
    '';

  return formatInsightNumbers(applyResolvedCategoryToText(raw, categoryName) ?? raw);
}

/** Alert type badge (e.g. "Spending Alert", "Income Analysis"). */
export function resolveInsightTypeBadge(item: InsightItemShape): string {
  return (
    item.type ??
    item.indicator ??
    'Insight'
  );
}

/** Category passed to Set Alert / Plan Buffer modal. */
export function resolveAlertModalCategory(
  item: InsightItemShape,
  categoryMap?: Map<string, string>
): string | undefined {
  return resolveSpendingCategoryName(item, categoryMap) ?? item.type;
}

/** Average confidence for a tab section — shown once in the section header. */
export function resolveSectionDataConfidence(items: InsightItemShape[]): number | null {
  const scores = items
    .map((item) => item.confidence ?? item.confidence_score)
    .filter((score): score is number => typeof score === 'number');
  if (scores.length === 0) return null;
  return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
}
